// src/views/student-words-list/StudentWordsListsView.tsx
import React, {
  FC, useCallback, useEffect, useMemo,
} from 'react';
import { Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { nanoid } from 'nanoid';
import { TbStack2 } from 'react-icons/tb';
import withTagManager from '../../hooks/withTagManager';
import styles from './StudentWordsListsView.module.less';
import {
  getIsLoadingWordsListList,
  getStudentListOfWordsList,
} from './slice/selectors';
import { fetchStudentWordsListPage, resetData } from './slice/actions';
import ListCard from '../../components/ListCard';
import { getUserData } from '../auth/slice/selectors';
import { isNewList, readOpenedListIds } from './openedLists';

const StudentWordsListsView: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loading = useSelector(getIsLoadingWordsListList);
  const lists = useSelector(getStudentListOfWordsList);
  const user = useSelector(getUserData);

  const filteredAndSortedLists = useMemo(() => {
  // Teraz po prostu sprawdź czy user.id jest w sharedStudentIds
    const filtered = lists.filter((list: any) => list.sharedStudentIds?.includes(user.id));

    // Od najnowszej daty udostępnienia temu uczniowi; przy równych datach wyższe id wyżej
    return filtered.sort((a: any, b: any) => {
      const dateA = new Date(a.shareMetadata?.sharedAt ?? 0).getTime();
      const dateB = new Date(b.shareMetadata?.sharedAt ?? 0).getTime();

      if (dateA !== dateB) {
        return dateB - dateA;
      }

      return b.id - a.id;
    });
  }, [lists, user.id]);

  // Listy, które uczeń już otwierał — raz przy wejściu na ekran; otwarcie listy i tak przenosi do gry
  const openedListIds = useMemo(() => readOpenedListIds(user.id), [user.id]);

  const loadMoreData = useCallback(() => {
    if (loading) {
      return;
    }
    dispatch(fetchStudentWordsListPage());
  }, [dispatch, loading]);

  useEffect(() => {
    dispatch(resetData());
    loadMoreData();
  }, []);

  return (
    <Spin spinning={loading} wrapperClassName={styles.spin}>
      <div className={styles.wrapper}>
        {/* Rozstrzelone wersaliki jak nagłówki na ekranie „Uczniowie”, liczba list w plakietce. Bez list — bez plakietki */}
        <div className={styles.header}>
          <span className={styles.headerTitle}>{t('words-list:studentLists.title')}</span>
          {filteredAndSortedLists.length > 0 && (
            <span className={styles.headerCount}>{filteredAndSortedLists.length}</span>
          )}
        </div>

        {filteredAndSortedLists.length > 0 && (
          <div className={styles.cardsGrid}>
            {filteredAndSortedLists.map((list: any) => (
              <ListCard list={list} isNew={isNewList(list, openedListIds)} key={nanoid()} />
            ))}
          </div>
        )}

        {/* Pusty stan jak na ekranie „Uczniowie”: ikona i napis na środku. W trakcie wczytywania nic — nie miga */}
        {filteredAndSortedLists.length === 0 && !loading && (
          <div className={styles.emptyState}>
            <TbStack2 className={styles.emptyIcon} />
            <p>{t('words-list:studentLists.empty')}</p>
            <p className={styles.emptyHint}>{t('words-list:studentLists.emptyHint')}</p>
          </div>
        )}
      </div>
    </Spin>
  );
};

export default withTagManager(StudentWordsListsView);
